import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useAiNotifications } from "@/hooks/useAi";

export default function NotificationBell({ basePath = "" }) {
  const { data, refetch } = useAiNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = data?.items || [];
  const unread = data?.unread || 0;

  const openNotif = async (n) => {
    if (!n.read) await api.post(`/ai/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    refetch();
    if (n.link) navigate(n.link.startsWith("/") ? n.link : `${basePath}${n.link}`);
  };

  const markAll = async () => {
    await api.post("/ai/notifications/read-all").catch(() => {});
    refetch();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) refetch(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="notif-bell"
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-surface hover:text-violet-600"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notif-bell-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-violet-600 px-1 text-[10px] font-black text-white ring-2 ring-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <p className="flex items-center gap-1.5 font-display text-sm font-extrabold text-ink">
            <Sparkles className="h-4 w-4 text-violet-600" /> Notifications IA
          </p>
          {unread > 0 && (
            <button data-testid="notif-mark-all" onClick={markAll} className="flex items-center gap-1 text-[11px] font-bold text-violet-700 hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Tout lire
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.length ? (
            items.map((n) => (
              <button
                key={n.id}
                data-testid="notif-item"
                onClick={() => openNotif(n)}
                className={`flex w-full gap-3 border-b border-line/70 px-4 py-3 text-left transition-colors hover:bg-surface ${n.read ? "" : "bg-violet-50/50"}`}
              >
                <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-violet-500"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-bold text-ink">{n.title}</span>
                  <span className="mt-0.5 block whitespace-pre-wrap text-[11.5px] leading-snug text-slate-500">{n.message}</span>
                  <span className="mt-1 block text-[10px] text-slate-400">{formatDateTime(n.created_at)}</span>
                </span>
              </button>
            ))
          ) : (
            <div className="px-4 py-10 text-center">
              <Bell className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-2 text-xs text-slate-400">Aucune notification pour le moment.</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
